import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { adsApi } from '@/api/client';
import { fetchAllSpAdGroups, fetchAllSpKeywords } from '@/utils/fetchAllSpCatalog';
import { parseCampaignsPayload } from '@/utils/entityCrossFilter/buildEntityGraph';
import {
  mergeAdGroups,
  parseAdGroupsListPayload,
  parseSdAdGroupsPayload,
} from '@/utils/unifiedAdGroups';
import type { KeywordEntityRow } from '@/api/unifiedKeywords';
import { parseKeywordsPayload } from '@/api/keywordMetrics';

const ALL_STATES = ['ENABLED', 'PAUSED', 'ARCHIVED'] as const;

export function useWorkspaceCatalog(profileId: string) {
  const [fetchAllCatalog, setFetchAllCatalog] = useState(false);

  const portfoliosQuery = useQuery({
    queryKey: ['profile-portfolios', profileId],
    queryFn: async () => {
      const res = await adsApi.listPortfolios(profileId);
      if (!res.success || !res.data) throw new Error('Failed to load portfolios');
      return res.data;
    },
    enabled: Boolean(profileId),
    staleTime: 60_000,
  });

  const campaignsQuery = useQuery({
    queryKey: ['profile-campaigns', profileId],
    queryFn: async () => {
      const res = await adsApi.fetchProfileCampaigns(profileId, null, {
        adProduct: 'ALL',
        status: 'ALL',
        search: '',
      });
      if (!res.success) throw new Error('Failed to load campaigns');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  const foundationReady =
    Boolean(profileId) && portfoliosQuery.isSuccess && campaignsQuery.isSuccess;

  const sbAdGroupsQuery = useQuery({
    queryKey: ['sb-ad-groups', profileId],
    queryFn: async () => {
      const res = await adsApi.listSbAdGroups(profileId, {
        include_extended_data_fields: true,
        states: [...ALL_STATES],
        max_results: 1000,
      });
      if (!res.success) throw new Error('Failed to load SB ad groups');
      return res.data;
    },
    enabled: foundationReady,
  });

  const spAdGroupsQuery = useQuery({
    queryKey: ['sp-ad-groups', profileId, fetchAllCatalog],
    queryFn: async () => {
      const result = await fetchAllSpAdGroups(
        profileId,
        {
          include_extended_data_fields: true,
          states: [...ALL_STATES],
          max_results: 1000,
        },
        { fetchAll: fetchAllCatalog }
      );
      return result;
    },
    enabled: foundationReady,
  });

  const spKeywordsQuery = useQuery({
    queryKey: ['sp-keywords', profileId, fetchAllCatalog],
    queryFn: async () => {
      const result = await fetchAllSpKeywords(
        profileId,
        {
          include_extended_data_fields: true,
          states: [...ALL_STATES],
          max_results: 1000,
        },
        { fetchAll: fetchAllCatalog }
      );
      return result;
    },
    enabled: foundationReady,
  });

  const sbKeywordsQuery = useQuery({
    queryKey: ['sb-keywords', profileId],
    queryFn: async () => {
      const res = await adsApi.getSbKeywords(profileId, { count: 1000 });
      if (!res.success) throw new Error('Failed to load SB keywords');
      return res.data;
    },
    enabled: foundationReady,
  });

  const spSbBatchReady =
    foundationReady &&
    sbAdGroupsQuery.isSuccess &&
    spAdGroupsQuery.isSuccess &&
    spKeywordsQuery.isSuccess &&
    sbKeywordsQuery.isSuccess;

  const sdAdGroupsQuery = useQuery({
    queryKey: ['sd-ad-groups', profileId],
    queryFn: async () => {
      const res = await adsApi.getSdAdGroups(profileId);
      if (!res.success) throw new Error('Failed to load SD ad groups');
      return res.data;
    },
    enabled: spSbBatchReady,
  });

  const campaigns = parseCampaignsPayload(campaignsQuery.data);
  const portfolios = portfoliosQuery.data?.portfolios ?? [];

  const sbAdGroups = parseAdGroupsListPayload(sbAdGroupsQuery.data).adGroups;
  const spAdGroupsResult = spAdGroupsQuery.data;
  const spAdGroups = spAdGroupsResult?.items ?? [];
  const sdAdGroups = parseSdAdGroupsPayload(sdAdGroupsQuery.data);

  const adGroups = mergeAdGroups(spAdGroups, sbAdGroups, sdAdGroups);

  const spKeywordsResult = spKeywordsQuery.data;
  const spKeywords: KeywordEntityRow[] = spKeywordsResult?.items ?? [];
  const sbKeywordsPayload = parseKeywordsPayload(sbKeywordsQuery.data);
  const sbKeywords: KeywordEntityRow[] = sbKeywordsPayload.keywords;

  const catalogIncomplete =
    Boolean(spKeywordsResult?.hasMore) || Boolean(spAdGroupsResult?.hasMore);

  const isLoading =
    portfoliosQuery.isLoading ||
    campaignsQuery.isLoading ||
    sbAdGroupsQuery.isLoading ||
    spAdGroupsQuery.isLoading ||
    sdAdGroupsQuery.isLoading ||
    spKeywordsQuery.isLoading ||
    sbKeywordsQuery.isLoading;

  const isSuccess =
    portfoliosQuery.isSuccess &&
    campaignsQuery.isSuccess &&
    sbAdGroupsQuery.isSuccess &&
    spAdGroupsQuery.isSuccess &&
    spKeywordsQuery.isSuccess &&
    sbKeywordsQuery.isSuccess &&
    sdAdGroupsQuery.isSuccess;

  const isError =
    portfoliosQuery.isError ||
    campaignsQuery.isError ||
    sbAdGroupsQuery.isError ||
    spAdGroupsQuery.isError ||
    sdAdGroupsQuery.isError ||
    spKeywordsQuery.isError ||
    sbKeywordsQuery.isError;

  const error =
    portfoliosQuery.error ??
    campaignsQuery.error ??
    sbAdGroupsQuery.error ??
    spAdGroupsQuery.error ??
    sdAdGroupsQuery.error ??
    spKeywordsQuery.error ??
    sbKeywordsQuery.error;

  const refetchAll = useCallback(() => {
    void portfoliosQuery.refetch();
    void campaignsQuery.refetch();
    void sbAdGroupsQuery.refetch();
    void spAdGroupsQuery.refetch();
    void sdAdGroupsQuery.refetch();
    void spKeywordsQuery.refetch();
    void sbKeywordsQuery.refetch();
  }, [
    portfoliosQuery,
    campaignsQuery,
    sbAdGroupsQuery,
    spAdGroupsQuery,
    sdAdGroupsQuery,
    spKeywordsQuery,
    sbKeywordsQuery,
  ]);

  const loadFullCatalog = useCallback(() => {
    setFetchAllCatalog(true);
  }, []);

  return {
    portfolios,
    campaigns,
    adGroups,
    spKeywords,
    sbKeywords,
    isLoading,
    isSuccess,
    isError,
    error,
    catalogIncomplete,
    fetchAllCatalog,
    loadFullCatalog,
    isFetchingFullCatalog:
      fetchAllCatalog && (spKeywordsQuery.isFetching || spAdGroupsQuery.isFetching),
    refetchAll,
  };
}
